import { apiClient, authApi } from '@/services/api'
import type { AppLanguage } from '@/services/i18n/languages'
import type {
  AudioDraft,
  AudioKind,
  AudioSettings,
  CaseMembership,
  ConnectionTest,
  LakeGrant,
  SftpResetResponse,
  VideoDraft,
  VideoKind,
  VideoSettings,
} from './types'

export function audioPayload(draft: AudioDraft) {
  const endpoint = (kind: AudioKind) => ({
    mode: draft[kind].mode,
    url: draft[kind].url,
    api_key: draft[kind].api_key || null,
  })
  return {
    revision: draft.revision,
    transcription: endpoint('transcription'),
    diarization: endpoint('diarization'),
  }
}

/** The draft as the API takes it: a secret left blank keeps the saved one. */
export function videoPayload(draft: VideoDraft) {
  return {
    revision: draft.revision,
    engine: { url: draft.engine.url, api_key: draft.engine.api_key },
    exchange: {
      endpoint_url: draft.exchange.endpoint_url,
      bucket: draft.exchange.bucket,
      tenant: draft.exchange.tenant,
      region: draft.exchange.region,
      access_key_id: draft.exchange.access_key_id,
      secret_access_key: draft.exchange.secret_access_key,
    },
  }
}

/** Server calls backing the user settings page. */
export const settingsApi = {
  resetSftpPassword: () => apiClient.post<SftpResetResponse>('/auth/me/sftp-password/reset', {}),
  getMemberships: () => apiClient.get<CaseMembership[]>('/v1/users/me/memberships'),
  getLakeGrants: () => apiClient.get<LakeGrant[]>('/v1/users/me/lake-grants'),
  updateLanguage: (language: AppLanguage) => authApi.updateMe({ preferred_language: language }),
  /** The operator's unit (HUMINT on only); null clears it. */
  updateUnit: (unit: string | null) => authApi.updateMe({ unit }),
  getAudio: () => apiClient.get<AudioSettings>('/v1/settings/audio'),
  saveAudio: (draft: AudioDraft) =>
    apiClient.put<AudioSettings>('/v1/settings/audio', audioPayload(draft)),
  testAudio: (draft: AudioDraft, kind: AudioKind) =>
    apiClient.post<ConnectionTest>(`/v1/settings/audio/test/${kind}`, audioPayload(draft)),
  getVideo: () => apiClient.get<VideoSettings>('/v1/settings/video'),
  saveVideo: (draft: VideoDraft) =>
    apiClient.put<VideoSettings>('/v1/settings/video', videoPayload(draft)),
  testVideo: (draft: VideoDraft, kind: VideoKind) =>
    apiClient.post<ConnectionTest>(`/v1/settings/video/test/${kind}`, videoPayload(draft)),
}
